import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Phone, CheckCircle2, MapPin } from "lucide-react";
import { COMPANY, SERVICES, SERVICE_PAGES, CITY_PAGES } from "@/lib/site-data";
import LogoEmblem from "@/components/LogoEmblem";
import CountUp from "@/components/CountUp";
import { useSeo, orgSchema } from "@/lib/useSeo";

const ease = [0.22, 1, 0.36, 1];

export default function CityPage() {
  const { slug } = useParams();
  const city = CITY_PAGES.find((c) => c.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const faqs = city
    ? [
        {
          q: `Quel est le prix d'une rénovation de toiture à ${city.name} ?`,
          a: "Chaque toiture est unique : le budget dépend du matériau, de la surface et de l'état de la charpente. Notre diagnostic sur le toit est gratuit et le devis, remis sous 48 h, est détaillé poste par poste — prix ferme, sans surprise.",
        },
        {
          q: `Intervenez-vous en urgence à ${city.name} ?`,
          a: `Oui : fuite, tuile envolée ou dégât de tempête, nous intervenons sous 2 heures à ${city.name}, 7 jours sur 7, avec mise hors d'eau et bâchage de protection si nécessaire.`,
        },
        {
          q: `Le démoussage est-il vraiment utile à ${city.name} ?`,
          a: "Oui. Le climat humide de la Gironde fait proliférer mousse et lichen qui retiennent l'eau et fendent les tuiles au gel. Un démoussage suivi d'un hydrofuge prolonge fortement la durée de vie de votre toit.",
        },
      ]
    : [];

  useSeo({
    title: city ? `Couvreur à ${city.name} — NB Couverture | Rénovation, Démoussage, Urgence 24/7` : undefined,
    description: city
      ? `Couvreur-zingueur à ${city.name} : rénovation de toiture, démoussage, hydrofuge, réparation de fuite en urgence sous 2 h, zinguerie, ardoise, tuile canal, zinc. Devis gratuit — ${COMPANY.phone}.`
      : undefined,
    canonical: city ? `/couvreur/${city.slug}` : undefined,
    schemas: city
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Accueil", item: "https://nb-couverture-bordeaux.fr/" },
              { "@type": "ListItem", position: 2, name: "Zones d'intervention", item: "https://nb-couverture-bordeaux.fr/#zones" },
              { "@type": "ListItem", position: 3, name: `Couvreur à ${city.name}`, item: `https://nb-couverture-bordeaux.fr/couvreur/${city.slug}` },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: `Travaux de couverture à ${city.name}`,
            description: city.intro,
            provider: orgSchema,
            areaServed: { "@type": "City", name: city.name },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]
      : [],
  });

  if (!city) return <Navigate to="/" replace />;

  const others = CITY_PAGES.filter((c) => c.slug !== city.slug);

  return (
    <div className="min-h-screen bg-[#0D0F12] text-slate-200 antialiased">
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#0D0F12]/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <Link to="/" data-testid="city-logo" className="flex items-center gap-3">
            <LogoEmblem size={40} />
            <span className="font-serif text-sm font-semibold text-white">NB Couverture</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="city-back-home"
              className="hidden items-center gap-2 text-xs font-semibold text-slate-300 transition-colors hover:text-terra sm:inline-flex"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Retour à l'accueil
            </Link>
            <a
              href={COMPANY.phoneHref}
              data-testid="city-phone-cta"
              className="inline-flex items-center gap-2 rounded-full bg-terra px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-terra-hover"
            >
              <Phone className="h-3.5 w-3.5" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-16 md:py-24">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease }}>
          <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-terra">
            <MapPin className="h-3.5 w-3.5" />
            Couvreur · Zingueur — {city.name}, Gironde
          </p>
          <h1 className="mt-5 font-serif text-4xl font-semibold leading-tight text-white md:text-6xl">
            Couvreur à <span className="italic text-terra">{city.name}</span>
          </h1>
          <p data-testid="city-intro" className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
            {city.intro}
          </p>
          <ul className="mt-6 flex flex-wrap gap-3">
            {city.points.map((p) => (
              <li
                key={p}
                className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs text-slate-200"
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-terra" />
                {p}
              </li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="/#devis"
              data-testid="city-devis-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-terra px-7 py-4 text-sm font-semibold text-white transition-colors duration-300 hover:bg-terra-hover"
            >
              Devis gratuit à {city.name}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href={COMPANY.phoneHref}
              className="inline-flex items-center gap-3 rounded-full border border-white/15 px-7 py-4 text-sm font-semibold text-slate-200 transition-colors duration-300 hover:border-terra/60 hover:text-white"
            >
              <Phone className="h-4 w-4 text-terra" />
              Urgence fuite 24/7
            </a>
          </div>

          <div className="mt-12 grid max-w-2xl grid-cols-3 gap-6 rounded border border-white/10 bg-black/50 px-5 py-6 backdrop-blur-md md:px-8">
            {[
              { value: "25+", label: "années de métier" },
              { value: "596", label: "travaux effectués" },
              { value: "2 h", label: "délai d'urgence" },
            ].map((s) => (
              <div key={s.label} data-testid={`city-stat-${s.label.replace(/\s/g, "-")}`}>
                <p className="font-serif text-3xl font-bold text-white md:text-4xl">
                  <CountUp value={s.value} />
                </p>
                <p className="mt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-white md:text-xs">{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease }}
          className="mt-16"
        >
          <h2 className="font-serif text-2xl font-semibold text-white md:text-3xl">
            Nos prestations à {city.name}
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {SERVICES.map((s) => {
              const sp = SERVICE_PAGES.find((p) => p.serviceId === s.id);
              return (
                <a
                  key={s.id}
                  href={sp ? `/services/${sp.slug}` : "/#services"}
                  data-testid={`city-service-${s.id}`}
                  className="group border border-white/5 bg-[#14171D] p-6 transition-colors duration-300 hover:border-terra/40"
                >
                  <s.icon className="h-6 w-6 text-terra" />
                  <h3 className="mt-4 font-serif text-base text-slate-100 transition-colors group-hover:text-terra">
                    {s.title} à {city.name}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-slate-400">{s.desc}</p>
                </a>
              );
            })}
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease }}
          data-testid="city-faq"
          className="mt-16"
        >
          <h2 className="font-serif text-2xl font-semibold text-white md:text-3xl">
            Questions fréquentes — couvreur à {city.name}
          </h2>
          <div className="mt-8 grid gap-4">
            {faqs.map((f) => (
              <div key={f.q} className="border border-white/5 bg-[#14171D] p-6">
                <h3 className="font-serif text-base text-slate-100 md:text-lg">{f.q}</h3>
                <p className="mt-2.5 text-xs leading-relaxed text-slate-400 md:text-sm">{f.a}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <section className="mt-16 border-t border-white/5 pt-10">
          <h2 className="font-serif text-xl font-semibold text-white">Nous intervenons aussi à</h2>
          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2.5">
            {others.map((c) => (
              <Link
                key={c.slug}
                to={`/couvreur/${c.slug}`}
                data-testid={`city-link-${c.slug}`}
                className="text-xs text-slate-400 underline-offset-4 transition-colors hover:text-terra hover:underline"
              >
                Couvreur à {c.name}
              </Link>
            ))}
          </div>
        </section>
      </main>

      <footer className="border-t border-white/5 py-8">
        <p className="mx-auto max-w-6xl px-6 text-xs text-slate-500">
          {COMPANY.name} — {COMPANY.address} · {COMPANY.phone} · Garantie décennale ·{" "}
          <Link to="/" className="text-slate-400 underline-offset-4 hover:text-terra hover:underline">
            Accueil
          </Link>
        </p>
      </footer>
    </div>
  );
}
